import config from "@/config";
import { io } from "socket.io-client";
import type { Router } from "vue-router";
import type { Auth } from "@/plugins/auth";
import type { APIError } from "@/api/API";
import type { Order } from "@/models/Order";
import { type TableData, TableStatus } from "@/api/Table";
import { useGlobalState } from "..";
import { useOrderActions } from "./order";
import { useTableActions } from "./table";

export const useSyncActions = () => {
  const stopAutoAPISync = () => {
    const {
      sync: { interval }
    } = useGlobalState();
    if (interval.value) {
      clearInterval(interval.value);
    }
    interval.value = null;
  };

  const stopSocketSync = () => {
    const {
      sync: { socket }
    } = useGlobalState();
    // Chiusura voluta dall'app: tolgo prima i listener, cosi' il disconnect che segue
    // non fa ripartire la riconnessione
    socket.value?.removeAllListeners();
    socket.value?.disconnect();
    socket.value = null;
  };

  const stopSync = () => {
    stopAutoAPISync();
    stopSocketSync();
  };

  const syncData = async (router: Router, auth: Auth) => {
    const { table } = useGlobalState();
    const { setTableData } = useTableActions();
    const { setTableOrder } = useOrderActions();

    try {
      await setTableData(router, auth);
      if (table.value.data?.status !== TableStatus.AVAILABLE) {
        await setTableOrder(router, auth);
      }
    } catch (e) {
      const error = e as APIError;
      if (error?.response?.status == 403) {
        stopSync();
        router.push({ name: "serviceUnavailable" });
      } else {
        throw error;
      }
    }
  };

  const startAutoAPISync = async (router: Router, auth: Auth) => {
    const {
      sync: { interval, initialized }
    } = useGlobalState();
    interval.value = setInterval(async () => {
      await syncData(router, auth);
    }, 30000) as unknown as number;
    if (!initialized.value) {
      initialized.value = true;
      await syncData(router, auth);
    }
  };

  const startSocketSync = (router: Router, auth: Auth) => {
    const {
      table,
      sync: { socket }
    } = useGlobalState();
    const { updateTableData } = useTableActions();
    const { updateOrder } = useOrderActions();

    if (!table.value.id) return;
    stopSocketSync(); // evita due socket (e handler doppi) se startSync e' richiamato senza stopSync
    socket.value = io(config.apiURL, {
      transports: ["websocket"],
      upgrade: false,
      query: {
        tableId: table.value.id
      },
      auth: {
        token: `Bearer ${auth.clientToken}`
      },
      autoConnect: false
    });

    socket.value.on("connect_error", err => console.log(`connect_error due to ${err.message}`));
    /* socket.value.on("connect", () => { console.log("is connected: " + socket.value?.connected); }); */

    socket.value.connect();

    socket.value.on("table-updated", (tableData: TableData) =>
      updateTableData(tableData, router, auth)
    );
    socket.value.on("order-updated", (order: Order) => {
      updateOrder(order, router, auth);
    });

    // Alla riconnessione recupero subito lo stato del tavolo, senza aspettare il polling: gli eventi
    // emessi mentre il socket era giu' sono persi
    let hasConnected = false;
    socket.value.on("connect", () => {
      if (hasConnected) {
        syncData(router, auth).catch(err => console.log(`sync after reconnect failed: ${err?.message}`));
      }
      hasConnected = true;
    });

    // Con "io server disconnect" (es. stop o deploy di un'istanza di api-appuser) socket.io non
    // si ricollega da solo: lo faccio io, con un ritardo casuale per non far ricollegare tutti i
    // client nello stesso istante. Per gli altri motivi (rete, timeout) la riconnessione e'
    // automatica e i listener restano validi.
    socket.value.on("disconnect", reason => {
      if (reason === "io server disconnect") {
        const disconnectedSocket = socket.value;
        setTimeout(() => {
          // non se nel frattempo il socket e' stato fermato o sostituito
          if (socket.value === disconnectedSocket) disconnectedSocket?.connect();
        }, Math.random() * 3000);
      }
    });
  };

  const startSync = async (router: Router, auth: Auth) => {
    await startAutoAPISync(router, auth);
    startSocketSync(router, auth);
  };

  return {
    startAutoAPISync,
    stopAutoAPISync,
    startSocketSync,
    stopSocketSync,
    startSync,
    stopSync
  };
};
